import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { superYachts } from '../../mocks/superYachts';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import CharterInquiryForm from './components/CharterInquiryForm';

export default function SuperYachtDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isNavVisible, setIsNavVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showInquiryForm, setShowInquiryForm] = useState(false);

  // Fix: Handle both string and number IDs
  const yacht = superYachts.find(y => {
    if (typeof y.id === 'string') {
      return y.id === id;
    }
    return y.id === Number(id);
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!yacht) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Yacht Not Found</h1>
          <button
            onClick={() => navigate('/yacht-charter')}
            className="px-8 py-3 bg-black text-white text-sm tracking-wider hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            BACK TO CHARTER
          </button>
        </div>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation isVisible={isNavVisible} setIsVisible={setIsNavVisible} />

      {/* Hero Image Gallery */}
      <section className="pt-24">
        <div className="px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            {/* Main Image */}
            <div className="relative w-full h-[600px] mb-4">
              <img
                src={yacht.images[selectedImage]}
                alt={yacht.name}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Thumbnail Gallery */}
            <div className="grid grid-cols-4 gap-4">
              {yacht.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`relative w-full h-32 overflow-hidden cursor-pointer ${
                    selectedImage === index ? 'ring-2 ring-black' : ''
                  }`}
                >
                  <img
                    src={image}
                    alt={`${yacht.name} view ${index + 1}`}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Yacht Details */}
      <section className="py-16">
        <div className="px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <div className="mb-8">
                  <h1 className="text-4xl lg:text-5xl font-serif mb-4">{yacht.name}</h1>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span>{yacht.lengthFeet} ft / {yacht.length}m</span>
                    <span>•</span>
                    <span>{yacht.guestCapacity} Guests</span>
                    <span>•</span>
                    <span>{yacht.crewSize} Crew</span>
                    <span>•</span>
                    <span>{yacht.yachtType}</span>
                  </div>
                </div>

                <div className="mb-12">
                  <h2 className="text-2xl font-serif mb-4">Overview</h2>
                  <p className="text-base text-gray-700 leading-relaxed">{yacht.description}</p>
                </div>

                <div className="mb-12">
                  <h2 className="text-2xl font-serif mb-6">Charter Highlights</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {yacht.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <i className="ri-check-line text-xl mt-0.5"></i>
                        <span className="text-sm text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-12">
                  <h2 className="text-2xl font-serif mb-6">Specifications</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs tracking-wider text-gray-500 mb-1">BUILDER</p>
                      <p className="text-sm">{yacht.specifications.builder}</p>
                    </div>
                    <div>
                      <p className="text-xs tracking-wider text-gray-500 mb-1">YEAR</p>
                      <p className="text-sm">{yacht.specifications.yearBuilt}</p>
                    </div>
                    <div>
                      <p className="text-xs tracking-wider text-gray-500 mb-1">BEAM</p>
                      <p className="text-sm">{yacht.specifications.beam}m</p>
                    </div>
                    <div>
                      <p className="text-xs tracking-wider text-gray-500 mb-1">DRAFT</p>
                      <p className="text-sm">{yacht.specifications.draft}m</p>
                    </div>
                    <div>
                      <p className="text-xs tracking-wider text-gray-500 mb-1">CRUISING SPEED</p>
                      <p className="text-sm">{yacht.specifications.cruisingSpeed} knots</p>
                    </div>
                    <div>
                      <p className="text-xs tracking-wider text-gray-500 mb-1">MAX SPEED</p>
                      <p className="text-sm">{yacht.specifications.maxSpeed} knots</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-32 bg-gray-50 p-8">
                  <div className="mb-6">
                    <p className="text-xs tracking-wider text-gray-500 mb-2">WEEKLY CHARTER RATE</p>
                    <p className="text-3xl font-serif">{formatPrice(yacht.weeklyRate)}</p>
                  </div>

                  <div className="mb-6">
                    <p className="text-xs tracking-wider text-gray-500 mb-2">PRIMARY CRUISING REGION</p>
                    <p className="text-base">{yacht.cruisingRegion}</p>
                  </div>

                  <div className="mb-8">
                    <p className="text-xs tracking-wider text-gray-500 mb-2">AVAILABLE SEASONS</p>
                    <div className="flex flex-wrap gap-2">
                      {yacht.availableSeasons.map((season, index) => (
                        <span key={index} className="px-3 py-1 bg-white text-xs tracking-wider">
                          {season}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => setShowInquiryForm(true)}
                    className="w-full px-6 py-4 bg-black text-white text-sm tracking-wider hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer mb-4"
                  >
                    INQUIRE ABOUT THIS CHARTER
                  </button>

                  <button
                    onClick={() => navigate('/yacht-charter')}
                    className="w-full px-6 py-4 border border-black text-sm tracking-wider hover:bg-black hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    BACK TO SEARCH
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form Modal */}
      {showInquiryForm && (
        <CharterInquiryForm
          yachtName={yacht.name}
          onClose={() => setShowInquiryForm(false)}
        />
      )}

      <Footer />
    </div>
  );
}
